import styled from 'styled-components';
import { DEFINE_OPTION } from '../../constants';
import { ContentContainer } from '../containers';
import { Input } from '../Input';
import DefineInputChip from './DefineInputChip';
import { SectionButtons } from '../Button';
import { CrisisAlert, SavingsOutlined } from '@mui/icons-material';
import { Zeros } from '../Icons';
import COLORS from '../../colors';
import { ReactElement } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { REDUX_KEY } from '../../redux/constants';
import { updateField } from '../../redux/features/campaignDetail/campaignDetailSlice';
import { isOnlyNumbers, removeCommas, roundTwoDecimals } from '../../util';

interface DefineInputProps {
  option: DEFINE_OPTION;
  visibleDurations: any[];
  onSplitAcrossDurations: () => void;
  onEnterDurationsManually: () => void;
}

const StyledInputContainer = styled.div`
  padding-top: 9px;
  width: 45%;
`;

const StyledChipContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding-top: 9px;
  padding-bottom: 9px;
`;

const StyledButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const DefineInput = ({
  option,
  visibleDurations,
  onSplitAcrossDurations,
  onEnterDurationsManually
}: DefineInputProps) => {
  const dispatch = useAppDispatch();
  const {
    grossBudget,
    targetTarp,
    targetImpact,
    usedBudget,
    usedTarps,
    usedImpacts
  } = useAppSelector((state: RootState) => state.detail);

  interface DefineOption {
    id: string;
    title: string;
    description: string;
    inputLabel: string;
    helperText: string;
    leftButtonLabel: string;
    rightButtonLabel: string;
    leftButtonValue: string;
    rightButtonValue: string;
    leftButtonIcon: ReactElement;
    rightButtonIcon: ReactElement;
  }

  const options: Record<string, DefineOption> = {
    [DEFINE_OPTION.TARPS]: {
      id: 'tarps',
      title: 'Enter Target Campaign TARPs',
      description: '',
      inputLabel: 'Campaign TARPs Target',
      helperText: 'Target Audience Rating Points (TARPs)',
      leftButtonLabel: 'Calculated Budget',
      rightButtonLabel: 'Calculated 000s',
      leftButtonValue: roundTwoDecimals(usedBudget).toLocaleString(undefined, {
        minimumFractionDigits: 2
      }),
      rightButtonValue: roundTwoDecimals(usedImpacts).toLocaleString(
        undefined,
        { minimumFractionDigits: 2 }
      ),
      leftButtonIcon: <SavingsOutlined />,
      rightButtonIcon: <Zeros />
    },
    [DEFINE_OPTION.BUDGET]: {
      id: 'budget',
      title: 'Enter Target Campaign Budget',
      description: '',
      inputLabel: 'Campaign Budget Target',
      helperText: 'Currency: NZD',
      leftButtonLabel: 'Calculated TARPs',
      rightButtonLabel: 'Calculated 000s',
      leftButtonValue: roundTwoDecimals(usedTarps).toLocaleString(undefined, {
        minimumFractionDigits: 2
      }),
      rightButtonValue: roundTwoDecimals(usedImpacts).toLocaleString(
        undefined,
        { minimumFractionDigits: 2 }
      ),
      leftButtonIcon: <CrisisAlert />,
      rightButtonIcon: <Zeros />
    },
    [DEFINE_OPTION.ZEROS]: {
      id: 'zeros',
      title: 'Enter Target Campaign Impacts (000s)',
      description: '',
      inputLabel: 'Campaign 000s Target',
      helperText: '000s of Impacts',
      leftButtonLabel: 'Calculated Budget',
      rightButtonLabel: 'Calculated TARPs',
      leftButtonValue: roundTwoDecimals(usedBudget).toLocaleString(undefined, {
        minimumFractionDigits: 2
      }),
      rightButtonValue: roundTwoDecimals(usedTarps).toLocaleString(undefined, {
        minimumFractionDigits: 2
      }),
      leftButtonIcon: <SavingsOutlined />,
      rightButtonIcon: <CrisisAlert />
    }
  };

  const currentOption = options[option];

  const handleButtonClick = (status: boolean): void => {
    if (status) {
      onSplitAcrossDurations();
    }

    if (!status) {
      onEnterDurationsManually();
    }
  };

  const getInputValue = (): string => {
    switch (option) {
      case DEFINE_OPTION.BUDGET:
        return grossBudget.toLocaleString();
      case DEFINE_OPTION.TARPS:
        return targetTarp.toLocaleString();
      case DEFINE_OPTION.ZEROS:
        return targetImpact.toLocaleString();
      default:
        return '';
    }
  };

  const mapOptionToReduxKey = (): REDUX_KEY => {
    switch (option) {
      case DEFINE_OPTION.BUDGET:
        return REDUX_KEY.GROSS_BUDGET;
      case DEFINE_OPTION.TARPS:
        return REDUX_KEY.TARGET_TARP;
      case DEFINE_OPTION.ZEROS:
        return REDUX_KEY.TARGET_IMPACT;
      default:
        return REDUX_KEY.GROSS_BUDGET;
    }
  };

  const handleInputUpdate = (value: string) => {
    if (!isOnlyNumbers(value)) {
      return;
    }

    const updatedField = updateField({
      fieldName: mapOptionToReduxKey(),
      value: Number(removeCommas(value.toString()))
    });

    dispatch(updatedField);
  };

  return (
    <ContentContainer>
      <h2>{currentOption.title}</h2>
      <p>{currentOption.description}</p>
      <StyledInputContainer>
        <Input
          label={currentOption.inputLabel}
          helperText={currentOption.helperText}
          value={getInputValue()}
          onChange={(value) => handleInputUpdate(value)}
        />
      </StyledInputContainer>
      {currentOption.leftButtonValue !== '0' &&
        currentOption.rightButtonValue !== '0' && (
          <StyledChipContainer>
            <DefineInputChip
              description={currentOption.leftButtonLabel}
              value={currentOption.leftButtonValue}
              icon={currentOption.leftButtonIcon}
            />
            <DefineInputChip
              description={currentOption.rightButtonLabel}
              value={currentOption.rightButtonValue}
              icon={currentOption.rightButtonIcon}
            />
          </StyledChipContainer>
        )}
      <StyledButtonContainer>
        <SectionButtons
          leftButtonLabel="Enter durations manually"
          rightButtonLabel="Split across durations"
          rightBackgroundColor={COLORS.PRIMARY_500}
          rightTextColor={COLORS.PRIMARY_800}
          rightHoverColor={COLORS.BUTTON_PRIMARY_HOVER}
          onToggleAll={handleButtonClick}
        />
      </StyledButtonContainer>
    </ContentContainer>
  );
};

export default DefineInput;
